/**
 * @file PageHeader.tsx
 * @description Standard page header used on every feature page.
 * Renders a title, optional subtitle, and an optional right-side action slot.
 * Automatically extracts and converts text-based "Back" buttons in children to a left-aligned arrow icon.
 */

import * as React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
  className?: string;
  onBack?: () => void;
  backUrl?: string;
  showBack?: boolean;
}

/**
 * Traverses elements recursively to locate and extract a "Back" button's action,
 * removing it from the children layout.
 */
function extractBackAction(children: React.ReactNode): {
  onClick?: () => void;
  newChildren: React.ReactNode;
} {
  let backOnClick: (() => void) | undefined = undefined;

  const traverse = (node: React.ReactNode): React.ReactNode => {
    if (!node) return node;

    if (Array.isArray(node)) {
      const mapped = node.map((child) => traverse(child)).filter((child) => child !== null);
      return mapped.length === 0 ? null : mapped;
    }

    if (React.isValidElement(node)) {
      const props = node.props as any;

      const isButton =
        typeof node.type === 'string' && (node.type === 'button' || node.type === 'a');
      const isReactButton =
        typeof node.type === 'function' || typeof node.type === 'object';

      let isBackButton = false;

      const hasBackText = (text: any): boolean => {
        if (typeof text === 'string' && /back/i.test(text)) return true;
        if (Array.isArray(text)) return text.some(hasBackText);
        if (React.isValidElement(text)) return hasBackText((text as React.ReactElement<any>).props.children);
        return false;
      };

      const checkChildren = (childNode: React.ReactNode): boolean => {
        if (!childNode) return false;
        if (typeof childNode === 'string') return /back/i.test(childNode);
        if (Array.isArray(childNode)) return childNode.some(checkChildren);
        if (React.isValidElement(childNode)) {
          const typeName =
            (childNode.type as any)?.name || (childNode.type as any)?.displayName || '';
          if (typeName === 'ArrowLeft' || typeName.includes('ArrowLeft')) return true;
          return checkChildren((childNode as React.ReactElement<any>).props.children);
        }
        return false;
      };

      if (isButton || isReactButton) {
        if (hasBackText(props.children) || checkChildren(props.children)) {
          isBackButton = true;
        }
      }

      if (isBackButton && props.onClick) {
        backOnClick = props.onClick;
        return null; // Remove back button from layout
      }

      if (props.children) {
        const newSubChildren = traverse(props.children);
        return React.cloneElement(node, { ...props }, newSubChildren);
      }
    }

    return node;
  };

  const newChildren = traverse(children);
  return { onClick: backOnClick, newChildren };
}

/**
 * PageHeader – use at the top of every page component.
 */
export const PageHeader = React.memo(function PageHeader({
  title,
  subtitle,
  children,
  className,
  onBack,
  backUrl,
  showBack,
}: PageHeaderProps) {
  const navigate = useNavigate();
  const { onClick: extractedOnClick, newChildren } = extractBackAction(children);

  const showBackButton = showBack || !!extractedOnClick || !!onBack || !!backUrl;

  const handleBack = () => {
    if (extractedOnClick) {
      extractedOnClick();
    } else if (onBack) {
      onBack();
    } else if (backUrl) {
      navigate(backUrl);
    } else {
      navigate(-1);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6',
        className,
      )}
    >
      <div className="flex items-start gap-4">
        {showBackButton && (
          <button
            type="button"
            onClick={handleBack}
            className="mt-1 flex items-center justify-center h-10 w-10 rounded-xl border border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground active:scale-95 transition-all cursor-pointer flex-shrink-0"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        <div className="space-y-1">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">{title}</h1>
          {subtitle && <p className="text-xs sm:text-sm text-muted-foreground">{subtitle}</p>}
        </div>
      </div>

      {newChildren && (
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
          {newChildren}
        </div>
      )}
    </div>
  );
});
